/**
 * Single HTML template for product catalog: preview iframe, browser print, and PDF (html2pdf).
 * Logo = custom upload only (no monogram). Running header/footer on every printed page.
 */

const DOC_TITLE = "Product catalogue";
const CONFIDENTIAL_LINE =
  "Confidential — for authorised partners only.";
/** Short line for repeating print footer only */
const RUNNING_FOOTER_SHORT = "Confidential · authorised partners only";

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const isPublicCatalogWebsite = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return false;
  try {
    const u = new URL(s.startsWith("http") ? s : `https://${s}`);
    const host = (u.hostname || "").toLowerCase();
    if (!host || host === "localhost" || host.endsWith(".localhost"))
      return false;
    const path = (u.pathname || "/").toLowerCase();
    if (path.includes("/dashboard")) return false;
    const qs = u.searchParams;
    if (qs.has("component")) return false;
    return true;
  } catch {
    return false;
  }
};

/** @returns {{ href: string, label: string } | null} */
const getPublicWebsiteLink = (raw) => {
  if (!isPublicCatalogWebsite(raw)) return null;
  const s = String(raw ?? "").trim();
  try {
    const u = new URL(s.startsWith("http") ? s : `https://${s}`);
    const href = u.toString();
    const host = (u.hostname || "").replace(/^www\./i, "");
    let path = u.pathname || "";
    if (path === "/") path = "";
    else path = path.replace(/\/$/, "");
    const label = (host + path + (u.search || "")).trim() || host;
    return { href, label };
  } catch {
    return null;
  }
};

/**
 * @param {object} catalog
 * @param {object | null | undefined} companyDetails Redux company profile (email, businessName, phoneNumber, address, gst)
 */
export function buildCatalogHtml(catalog, companyDetails) {
  const co =
    companyDetails && typeof companyDetails === "object" ? companyDetails : {};
  const meta = catalog?.meta ?? {};
  const logoUrl = catalog?.logoDataUrl || "";
  const businessNameRaw =
    String(co.businessName ?? meta.businessName ?? "").trim() || "Company name";
  const company = escapeHtml(businessNameRaw);
  const titleText = escapeHtml(DOC_TITLE);
  const footerNote = escapeHtml(meta.footerNote || "");
  const footnoteDisclaimerRaw = String(meta.footnoteDisclaimer ?? "").trim();
  const footnoteDisclaimerHtml = footnoteDisclaimerRaw
    ? escapeHtml(footnoteDisclaimerRaw)
    : "";
  const schemeDisclaimer = escapeHtml(meta.schemeDisclaimer || "");
  const taglineRaw = String(meta.tagline ?? "").trim();
  const taglineHtml = taglineRaw ? escapeHtml(taglineRaw) : "";
  const introRaw = String(meta.intro ?? "").trim();
  const introHtml = introRaw
    ? introRaw
        .split(/\n/)
        .map((line) => escapeHtml(line))
        .join("<br/>")
    : "";
  const webLink = getPublicWebsiteLink(meta.website);

  const phoneRaw = String(co.phoneNumber ?? meta.phone ?? "").trim();
  const phoneDisp = phoneRaw ? escapeHtml(phoneRaw) : "";
  const phoneTelHref = phoneRaw
    ? `tel:${phoneRaw.replace(/[\s().-]/g, "")}`
    : "";
  const emailRaw = String(co.email ?? meta.email ?? "").trim();
  const emailDisp = emailRaw ? escapeHtml(emailRaw) : "";
  const mailtoHref = emailRaw.includes("@") ? `mailto:${emailRaw}` : "";
  const addressRaw = String(co.address ?? meta.address ?? "").trim();
  const addressHtml = addressRaw
    ? addressRaw
        .split(/\n/)
        .map((l) => escapeHtml(l))
        .join("<br/>")
    : "";
  const gstRaw = String(co.gst ?? "").trim();
  const gstDisp = gstRaw ? escapeHtml(gstRaw) : "";

  const metaContactPieces = [];
  if (webLink) {
    metaContactPieces.push(
      `<div class="masthead-contact-item masthead-contact-item--wide">
        <span class="masthead-contact-label">Website</span>
        <div class="masthead-contact-value">
          <span class="masthead-web-pill">
            <a class="web-link" href="${escapeHtml(
              webLink.href
            )}" rel="noopener noreferrer" target="_blank">${escapeHtml(
        webLink.label
      )}</a>
          </span>
        </div>
      </div>`
    );
  }
  if (phoneDisp) {
    const phBody = phoneTelHref
      ? `<a class="masthead-contact-link" href="${escapeHtml(
          phoneTelHref
        )}">${phoneDisp}</a>`
      : `<span class="masthead-contact-text">${phoneDisp}</span>`;
    metaContactPieces.push(
      `<div class="masthead-contact-item">
        <span class="masthead-contact-label">Phone</span>
        <div class="masthead-contact-value">${phBody}</div>
      </div>`
    );
  }
  if (emailDisp) {
    const emBody = mailtoHref
      ? `<a class="masthead-contact-link" href="${escapeHtml(
          mailtoHref
        )}">${emailDisp}</a>`
      : `<span class="masthead-contact-text">${emailDisp}</span>`;
    metaContactPieces.push(
      `<div class="masthead-contact-item">
        <span class="masthead-contact-label">Email</span>
        <div class="masthead-contact-value">${emBody}</div>
      </div>`
    );
  }
  if (addressHtml) {
    metaContactPieces.push(
      `<div class="masthead-contact-item masthead-contact-item--wide">
        <span class="masthead-contact-label">Address</span>
        <div class="masthead-contact-value masthead-contact-value--multiline">${addressHtml}</div>
      </div>`
    );
  }
  if (gstDisp) {
    metaContactPieces.push(
      `<div class="masthead-contact-item">
        <span class="masthead-contact-label">GST</span>
        <div class="masthead-contact-value"><span class="masthead-contact-text">${gstDisp}</span></div>
      </div>`
    );
  }
  const mastheadMetaBar =
    metaContactPieces.length > 0
      ? `<div class="masthead-meta-bar"><div class="masthead-contact-grid">${metaContactPieces.join(
          ""
        )}</div></div>`
      : "";

  const columns = Array.isArray(catalog?.columns) ? catalog.columns : [];
  const rows = Array.isArray(catalog?.rows) ? catalog.rows : [];

  const headCells = columns
    .map(
      (c) =>
        `<th>${escapeHtml(c.label != null ? String(c.label) : "")}</th>`
    )
    .join("");

  const bodyRows = rows.length
    ? rows
        .map((r, i) => {
          const cells = columns
            .map((c) => {
              const v = r?.cells?.[c.id];
              return `<td>${escapeHtml(v != null ? String(v) : "")}</td>`;
            })
            .join("");
          return `<tr><td class="num">${i + 1}</td>${cells}</tr>`;
        })
        .join("")
    : `<tr><td class="num">—</td><td colspan="${Math.max(
        columns.length,
        1
      )}" class="muted-cell">Add rows in the editor.</td></tr>`;

  const schemeBodies = (catalog?.schemes ?? [])
    .map((s) => String(s?.body ?? "").trim())
    .filter(Boolean);

  const schemeItems = schemeBodies.length
    ? schemeBodies.map((t) => `<li>${escapeHtml(t)}</li>`).join("")
    : '<li class="muted">Add scheme lines in the editor.</li>';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${titleText}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"/>
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
<link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,400&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap" rel="stylesheet"/>
<style>
  :root {
    --ink: #1c1917;
    --ink-soft: #292524;
    --muted: #78716c;
    --muted-deep: #57534e;
    --rule: rgba(28,25,23,.1);
    --rule-strong: rgba(28,25,23,.18);
    --paper: #faf8f5;
    --paper-deep: #f5f0e8;
    --cream: #fffefb;
    --accent: #a67c52;
    --accent-dark: #7d5a3a;
    --table-head: #252320;
    --table-head-fg: #f5f2eb;
    --font-display: "Cormorant Garamond", "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif;
    --font-body: "DM Sans", ui-sans-serif, system-ui, -apple-system, sans-serif;
    --header-h: 34px;
    --footer-h: 22px;
    --page-margin-top: 48px;
    --page-margin-bottom: 38px;
  }
  * { box-sizing: border-box; }
  @page { margin: var(--page-margin-top) 14mm var(--page-margin-bottom) 14mm; }
  html, body {
    margin: 0; padding: 0;
    color: var(--ink);
    background: var(--paper);
    font-family: var(--font-body);
    font-size: 10.25pt;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-running-header, .print-running-footer { display: none; }

  .sheet-outer {
    min-height: 100vh;
    padding: 14mm 0 18mm;
    background:
      radial-gradient(ellipse 120% 80% at 50% -20%, rgba(166,124,82,.07) 0%, transparent 55%),
      linear-gradient(180deg, #ebe6dd 0%, var(--paper) 22%, var(--paper) 100%);
  }

  .sheet {
    max-width: 204mm;
    margin: 0 auto;
    padding: 11mm 13mm 14mm;
    background: var(--cream);
    border-radius: 2px;
    border: 1px solid var(--rule);
    box-shadow:
      0 1px 0 rgba(255,255,255,.9) inset,
      0 22px 48px rgba(28,25,23,.07),
      0 0 0 1px rgba(255,255,255,.35);
  }

  /* One masthead card: logo | copy aligned; website in full-width bar inside same box */
  .masthead-card {
    position: relative;
    margin-bottom: 16px;
    border-radius: 10px;
    border: 1px solid rgba(166,124,82,.38);
    background: linear-gradient(165deg, #fffefb 0%, #f7f2eb 55%, #faf7f2 100%);
    box-shadow:
      0 1px 0 rgba(255,255,255,.9) inset,
      0 10px 36px rgba(28,25,23,.07),
      0 0 0 1px rgba(255,255,255,.45);
    overflow: hidden;
  }
  .masthead-card::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      var(--accent) 0%,
      #c9986a 18%,
      rgba(166,124,82,.2) 55%,
      transparent 100%
    );
    pointer-events: none;
  }
  .masthead-card-grid {
    display: grid;
    grid-template-columns: 188px minmax(0, 1fr);
    grid-template-rows: auto auto;
    align-items: stretch;
  }
  .masthead-logo-cell {
    grid-column: 1;
    grid-row: 1;
    position: relative;
    padding: 0;
    border-right: 1px solid rgba(28,25,23,.1);
    background: rgba(255,254,252,.65);
    min-height: 148px;
    align-self: stretch;
  }
  .masthead-logo-frame {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 12px;
    box-sizing: border-box;
  }
  .masthead-main-col {
    grid-column: 2;
    grid-row: 1;
    min-width: 0;
    padding: 22px 22px 20px 24px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .masthead-panel {
    border: none;
    border-radius: 0;
    overflow: visible;
    box-shadow: none;
    background: transparent;
    padding: 0;
    position: relative;
  }
  .masthead-panel-head {
    position: relative;
    padding: 0 0 18px 0;
    text-align: left;
    background: transparent;
    color: var(--ink);
  }
  .masthead-panel-head::before {
    content: "";
    display: block;
    width: min(108px, 40%);
    height: 2px;
    margin-bottom: 14px;
    background: linear-gradient(
      90deg,
      var(--ink) 0%,
      var(--ink) 38%,
      var(--accent) 100%
    );
    border-radius: 1px;
    opacity: 0.9;
  }
  .masthead-panel-head::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(125,90,58,.45) 0%,
      rgba(28,25,23,.08) 50%,
      transparent 100%
    );
  }
  .masthead-eyebrow {
    margin: 0 0 10px;
    font-family: var(--font-body);
    font-size: 8pt;
    font-weight: 700;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: var(--accent-dark);
  }
  .masthead-company {
    font-family: var(--font-display);
    font-size: clamp(21pt, 4vw, 28pt);
    font-weight: 600;
    letter-spacing: 0.006em;
    line-height: 1.08;
    color: var(--ink);
  }
  .masthead-tagline {
    margin: 12px 0 0;
    font-family: var(--font-display);
    font-size: 12pt;
    font-style: italic;
    font-weight: 500;
    line-height: 1.4;
    color: var(--muted-deep);
    max-width: 56ch;
  }
  .masthead-panel-body {
    margin-top: 18px;
    margin-left: 0;
    padding: 14px 16px 16px 18px;
    background: rgba(255,254,252,.75);
    border: 1px solid rgba(28,25,23,.06);
    border-left: 3px solid var(--accent);
    border-radius: 0 8px 8px 0;
    box-shadow: 0 2px 16px rgba(28,25,23,.04);
  }
  .masthead-intro {
    margin: 0;
    max-width: 62ch;
    font-size: 9.85pt;
    line-height: 1.58;
    color: var(--muted-deep);
    font-weight: 450;
  }
  .masthead-logo {
    display: block;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: center center;
  }
  .masthead-logo-placeholder {
    width: 100%;
    height: 100%;
    min-height: 0;
    border: 1px dashed rgba(125,90,58,.4);
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--muted);
    font-family: var(--font-body);
    font-size: 7.5pt;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    padding: 12px;
    text-align: center;
    background: rgba(255,254,251,.5);
    box-sizing: border-box;
  }
  .masthead-meta-bar {
    grid-column: 1 / -1;
    grid-row: 2;
    padding: 14px 22px 16px;
    border-top: 1px solid rgba(28,25,23,.1);
    background: linear-gradient(180deg, rgba(250,246,239,.98) 0%, rgba(243,236,226,.92) 100%);
  }
  .masthead-contact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(176px, 1fr));
    gap: 14px 28px;
    width: 100%;
    align-items: start;
  }
  .masthead-contact-item {
    min-width: 0;
  }
  .masthead-contact-item--wide {
    grid-column: 1 / -1;
  }
  .masthead-contact-label {
    display: block;
    margin-bottom: 5px;
    font-family: var(--font-body);
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .masthead-contact-value {
    font-size: 9.25pt;
    line-height: 1.4;
    color: var(--ink-soft);
    font-weight: 500;
  }
  .masthead-contact-value--multiline {
    line-height: 1.45;
  }
  .masthead-contact-text {
    color: inherit;
  }
  .masthead-contact-link {
    color: var(--accent-dark);
    font-weight: 600;
    text-decoration: none;
  }
  .masthead-contact-link:hover {
    color: var(--ink-soft);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .masthead-web-pill {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-width: 0;
    padding: 6px 12px 6px 10px;
    border-radius: 999px;
    background: rgba(255,254,252,.95);
    border: 1px solid rgba(166,124,82,.35);
    box-shadow: 0 1px 3px rgba(28,25,23,.05);
  }
  .masthead-web-pill .web-link {
    min-width: 0;
  }
  .web-link {
    color: var(--accent-dark);
    font-weight: 600;
    font-size: 9pt;
    text-decoration: none;
    border-bottom: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .web-link:hover {
    color: var(--ink-soft);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .meta-kv {
    display: inline-flex;
    gap: 8px;
    align-items: baseline;
    padding: 4px 10px;
    background: rgba(245,240,232,.65);
    border-radius: 4px;
    border: 1px solid rgba(28,25,23,.06);
  }
  .meta-kv .k {
    font-weight: 700;
    font-size: 7.5pt;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
  }

  .catalog-panel {
    margin-top: 14px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(166,124,82,.42);
    box-shadow:
      0 12px 40px rgba(28,25,23,.08),
      0 0 0 1px rgba(255,255,255,.5) inset,
      0 1px 0 rgba(255,255,255,.85) inset;
  }
  .catalog-panel-head {
    position: relative;
    padding: 14px 18px 12px;
    text-align: left;
    background:
      radial-gradient(ellipse 60% 120% at 100% 0%, rgba(166,124,82,.14) 0%, transparent 50%),
      linear-gradient(175deg, #35302c 0%, #252220 50%, #1a1816 100%);
    color: #faf6ef;
  }
  .catalog-panel-head::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(166,124,82,.5) 45%,
      var(--accent) 50%,
      rgba(166,124,82,.5) 55%,
      transparent 100%
    );
  }
  .catalog-panel-ornament {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  .catalog-panel-ornament span {
    display: block;
    width: 4px;
    height: 4px;
    background: linear-gradient(135deg, #e8d4b8, var(--accent));
    transform: rotate(45deg);
    box-shadow: 0 0 0 1px rgba(0,0,0,.25);
  }
  .catalog-panel-ornament::after {
    content: "";
    flex: 1;
    max-width: 40px;
    height: 1px;
    margin-left: 4px;
    background: linear-gradient(90deg, rgba(232,212,184,.5), transparent);
  }
  .catalog-panel-eyebrow {
    margin: 0 0 4px;
    font-family: var(--font-body);
    font-size: 7pt;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(250,246,239,.52);
  }
  .catalog-panel-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: 15pt;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1.2;
    color: #fffefb;
  }
  .catalog-panel-title em {
    font-style: italic;
    font-weight: 500;
    color: #e8d4b8;
  }
  .catalog-panel-body {
    background: var(--cream);
    overflow: hidden;
  }
  .products-wrap {
    margin: 0;
    border-radius: 0;
    overflow: hidden;
    background: var(--cream);
    border: none;
    box-shadow: none;
  }
  table.catalog {
    width: 100%;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;
  }
  .catalog thead th {
    text-align: left;
    font-family: var(--font-body);
    font-size: 7.5pt;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--table-head-fg);
    font-weight: 600;
    padding: 11px 12px;
    border-bottom: 1px solid rgba(0,0,0,.2);
    background: linear-gradient(180deg, #35302c 0%, var(--table-head) 48%, #1a1816 100%);
    text-shadow: 0 1px 0 rgba(0,0,0,.2);
  }
  .catalog thead th.num {
    width: 40px;
    text-align: center;
    color: rgba(245,242,235,.85);
    border-right: 1px solid rgba(255,255,255,.12);
  }
  .catalog tbody td {
    padding: 11px 13px;
    vertical-align: top;
    border-bottom: 1px solid rgba(28,25,23,.06);
    font-size: 10pt;
    font-weight: 450;
    color: var(--ink-soft);
  }
  .catalog tbody tr:nth-child(even) td {
    background: rgba(252,250,246,.92);
  }
  .catalog tbody tr:hover td {
    background: rgba(245,236,220,.35);
  }
  .catalog tbody tr:last-child td { border-bottom: none; }
  .catalog td.num {
    text-align: center;
    color: var(--muted-deep);
    font-size: 8.75pt;
    font-weight: 700;
    width: 40px;
    font-variant-numeric: tabular-nums;
    background: rgba(245,240,232,.5) !important;
    border-right: 1px solid rgba(28,25,23,.06);
  }
  .catalog td.muted-cell {
    color: var(--muted);
    font-style: italic;
    font-weight: 400;
  }

  .scheme-wrap {
    margin-top: 22px;
    page-break-inside: avoid;
  }
  .scheme-panel {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(166,124,82,.42);
    box-shadow:
      0 12px 40px rgba(28,25,23,.09),
      0 0 0 1px rgba(255,255,255,.5) inset,
      0 1px 0 rgba(255,255,255,.85) inset;
    page-break-inside: avoid;
  }
  .scheme-panel-head {
    position: relative;
    padding: 22px 24px 20px;
    text-align: center;
    background:
      radial-gradient(ellipse 80% 120% at 50% 0%, rgba(166,124,82,.18) 0%, transparent 55%),
      linear-gradient(175deg, #3a3531 0%, #252220 38%, #171514 100%);
    color: #faf6ef;
  }
  .scheme-panel-head::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(212,184,150,.35) 20%,
      var(--accent) 50%,
      rgba(212,184,150,.35) 80%,
      transparent 100%
    );
  }
  .scheme-panel-ornament {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }
  .scheme-panel-ornament span {
    display: block;
    width: 5px;
    height: 5px;
    background: linear-gradient(135deg, #e8d4b8, var(--accent));
    transform: rotate(45deg);
    box-shadow: 0 0 0 1px rgba(0,0,0,.25);
  }
  .scheme-panel-ornament::before,
  .scheme-panel-ornament::after {
    content: "";
    height: 1px;
    width: 42px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(232,212,184,.55),
      rgba(232,212,184,.55),
      transparent
    );
  }
  .scheme-panel-eyebrow {
    margin: 0 0 8px;
    font-family: var(--font-body);
    font-size: 7.5pt;
    font-weight: 600;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: rgba(250,246,239,.58);
  }
  .scheme-panel-title {
    margin: 0;
    padding: 0 8px;
    font-family: var(--font-display);
    font-size: clamp(17pt, 4.2vw, 22pt);
    font-weight: 500;
    line-height: 1.18;
    letter-spacing: 0.015em;
    font-feature-settings: "kern" 1, "liga" 1;
    page-break-after: avoid;
  }
  .scheme-title-first {
    color: #fffefb;
    font-weight: 600;
  }
  .scheme-title-rest {
    font-style: italic;
    font-weight: 400;
    color: #e8d4b8;
  }
  .scheme-head-rule {
    width: 56px;
    height: 2px;
    margin: 14px auto 0;
    border-radius: 1px;
    background: linear-gradient(90deg, transparent, var(--accent), transparent);
    opacity: 0.9;
  }
  .scheme-panel-body {
    padding: 18px 20px 16px;
    background:
      radial-gradient(ellipse 90% 60% at 18% 0%, rgba(166,124,82,.06) 0%, transparent 45%),
      linear-gradient(180deg, #fffefb 0%, #f7f2ea 100%);
    border-top: 1px solid rgba(28,25,23,.06);
  }
  .scheme-list {
    margin: 0;
    padding: 0;
    list-style: none;
    counter-reset: scheme;
    color: var(--ink-soft);
    font-size: 10.25pt;
    line-height: 1.48;
    font-weight: 450;
  }
  .scheme-list li {
    counter-increment: scheme;
    position: relative;
    margin: 0;
    padding: 0 4px 16px 44px;
    min-height: 32px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .scheme-list li:not(:last-child) {
    border-bottom: 1px solid rgba(28,25,23,.08);
  }
  .scheme-list li:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
  .scheme-list li::before {
    content: counter(scheme, decimal-leading-zero);
    position: absolute;
    left: 0;
    top: 1px;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    font-family: var(--font-body);
    font-size: 8.5pt;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #fffefb;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(155deg, #c9986a 0%, var(--accent-dark) 52%, #5c4030 100%);
    box-shadow:
      0 3px 10px rgba(92,64,48,.28),
      inset 0 1px 0 rgba(255,255,255,.28);
    border: 1px solid rgba(0,0,0,.12);
  }

  .scheme-list li.muted {
    counter-increment: none;
    padding: 4px 8px 0;
    text-align: center;
    font-style: italic;
    color: var(--muted);
    font-size: 9.75pt;
    border-bottom: none;
    min-height: 0;
  }
  .scheme-list li.muted::before {
    display: none;
  }

  .scheme-disclaimer {
    margin: 18px 0 0;
    padding: 12px 14px;
    font-size: 9pt;
    color: var(--muted-deep);
    line-height: 1.45;
    break-inside: avoid;
    page-break-inside: avoid;
    border-radius: 6px;
    border: 1px dashed rgba(166,124,82,.35);
    background: rgba(255,254,252,.72);
  }

  .footnotes { margin-top: 16px; font-size: 9pt; color: var(--muted-deep); }
  .disclaimer {
    margin-top: 6px;
    padding: 12px 14px;
    border: 1px solid rgba(166,124,82,.28);
    border-radius: 6px;
    background: linear-gradient(135deg, rgba(255,252,247,.95), rgba(245,238,228,.55));
    break-inside: avoid;
    page-break-inside: avoid;
    line-height: 1.45;
    font-style: italic;
  }

  .screen-footer {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid var(--rule);
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    font-size: 8pt;
    color: var(--muted);
    letter-spacing: 0.02em;
    line-height: 1.4;
  }
  .screen-footer strong {
    font-weight: 600;
    color: var(--ink-soft);
  }

  @media print {
    .sheet-outer {
      min-height: 0;
      padding: 0;
      background: var(--cream);
    }
    .sheet {
      max-width: none;
      margin: 0;
      padding: 0 0 9mm;
      padding-top: calc(var(--header-h) + 10px);
      border: none;
      box-shadow: none;
      border-radius: 0;
      background: var(--cream);
    }
    .screen-footer { display: none; }
    .catalog tbody tr:hover td { background: inherit; }

    .print-running-header, .print-running-footer {
      display: flex;
      position: fixed;
      left: 0;
      right: 0;
      z-index: 1000;
      align-items: center;
      padding: 6px 14mm;
      font-size: 7pt;
      color: var(--muted-deep);
      background: #fffefb;
    }
    .print-running-header {
      top: 0;
      min-height: var(--header-h);
      justify-content: flex-start;
      border-bottom: 1px solid rgba(166,124,82,.28);
    }
    .print-running-footer {
      bottom: 0;
      min-height: var(--footer-h);
      justify-content: space-between;
      border-top: 1px solid rgba(166,124,82,.28);
      background: linear-gradient(0deg, #faf8f5 0%, #fffefb 100%);
    }
    .running-head-line {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
      flex: 1;
    }
    .running-logo {
      max-height: 24px;
      max-width: 56px;
      object-fit: contain;
      object-position: left center;
      flex-shrink: 0;
    }
    .running-company-name {
      font-family: var(--font-display);
      font-weight: 600;
      color: var(--ink);
      font-size: 10pt;
      line-height: 1.15;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
    .running-foot-doc {
      font-family: var(--font-body);
      font-size: 6.5pt;
      font-weight: 600;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--muted);
    }
    .running-foot-tag {
      font-size: 6.5pt;
      color: var(--muted-deep);
      font-weight: 500;
      text-align: right;
      max-width: 55%;
    }

    .masthead-card {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    table.catalog thead { display: table-header-group; }
    table.catalog tr { page-break-inside: avoid; break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="print-running-header" aria-hidden="true">
    <div class="running-head-line">
      ${
        logoUrl
          ? `<img class="running-logo" src="${escapeHtml(logoUrl)}" alt="" />`
          : ""
      }
      <span class="running-company-name">${company}</span>
    </div>
  </div>
  <div class="print-running-footer" aria-hidden="true">
    <span class="running-foot-doc">${titleText}</span>
    <span class="running-foot-tag">${escapeHtml(RUNNING_FOOTER_SHORT)}</span>
  </div>

  <div class="sheet-outer">
  <div class="sheet">
    <div class="masthead-card">
      <div class="masthead-card-grid">
        <div class="masthead-logo-cell">
          <div class="masthead-logo-frame">
          ${
            logoUrl
              ? `<img class="masthead-logo" src="${escapeHtml(
                  logoUrl
                )}" alt="Logo"/>`
              : `<div class="masthead-logo-placeholder">Your logo</div>`
          }
          </div>
        </div>
        <div class="masthead-main-col">
          <div class="masthead-panel">
            <div class="masthead-panel-head">
              <p class="masthead-eyebrow">${titleText}</p>
              <div class="masthead-company">${company}</div>
              ${
                taglineHtml
                  ? `<p class="masthead-tagline">${taglineHtml}</p>`
                  : ""
              }
            </div>
            ${
              introHtml
                ? `<div class="masthead-panel-body"><p class="masthead-intro">${introHtml}</p></div>`
                : ""
            }
          </div>
        </div>
        ${mastheadMetaBar}
      </div>
    </div>

    <section class="catalog-panel">
      <header class="catalog-panel-head">
        <div class="catalog-panel-ornament" aria-hidden="true"><span></span><span></span><span></span></div>
        <p class="catalog-panel-eyebrow">Line card</p>
        <h2 class="catalog-panel-title">Product <em>range</em></h2>
      </header>
      <div class="catalog-panel-body">
        <div class="products-wrap">
          <table class="catalog">
            <thead>
              <tr>
                <th class="num">#</th>
                ${headCells}
              </tr>
            </thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </div>
      </div>
    </section>

    <section class="scheme-wrap">
      <div class="scheme-panel">
        <header class="scheme-panel-head">
          <div class="scheme-panel-ornament" aria-hidden="true"><span></span><span></span><span></span></div>
          <p class="scheme-panel-eyebrow">Authorised partners</p>
          <h2 class="scheme-panel-title"><span class="scheme-title-first">Partner programmes</span><span class="scheme-title-rest"> &amp; schemes</span></h2>
          <div class="scheme-head-rule" aria-hidden="true"></div>
        </header>
        <div class="scheme-panel-body">
          <ol class="scheme-list">${schemeItems}</ol>
          ${
            schemeDisclaimer
              ? `<p class="scheme-disclaimer">${schemeDisclaimer}</p>`
              : ""
          }
        </div>
      </div>
    </section>

    ${
      footnoteDisclaimerHtml
        ? `<div class="footnotes"><div class="disclaimer">${footnoteDisclaimerHtml}</div></div>`
        : ""
    }

    <footer class="screen-footer">
      <span><strong>${escapeHtml(CONFIDENTIAL_LINE)}</strong></span>
      <span>${footerNote || ""}</span>
    </footer>
  </div>
  </div>
</body>
</html>`;
}
